<template>
  <div>
    <Filters v-model="filterParams" :catalogs="catalogs" :loading="loading" />
    <md-card v-if="!!filterParams.warehouse && !!filterParams.purchaseOrder">
      <md-toolbar :class="toolbarClass()" class="md-layout md-elevation-0">
        <h3 class="md-layout-item" style="margin: 0">
          {{ isTransfer ? "Traspaso:" : "Orden de compra:" }}
          {{ currentPO.folio }}
        </h3>
        <h4 class="md-layout-item ml-auto text-right">{{ toolbarMessage() }}</h4>
      </md-toolbar>
      <md-card-content>
        <PurchaseOrderCard
          v-model="currentPO"
          :hasAllPhysicalEntries="hasAllPhysicalEntries"
          :partialMode="actionsPayload.parcialInputsMode"
          :hasNoPhysicalEntries="hasNoPhysicalEntries"
          :isTransfer="isTransfer"
          :loading="loading"
          @changeDeliveryDate="changeDeliveryDate"
        />
      </md-card-content>
      <md-card-actions>
        <PurchaseOrderActions
          v-if="!currentPO.residentValidation && canAddMaterialsquotation"
          v-model="actionsPayload"
          :isTransfer="isTransfer"
          :currentPO="currentPO"
          :isValid="validReceivedAmount"
          :validRemainingQuantity="validRemainingQuantity"
          :hasAllPhysicalEntries="hasAllPhysicalEntries"
          :hasNoPhysicalEntries="hasNoPhysicalEntries"
          :loading="loading"
          :comment="selectionComment"
          @sendData="sendData"
        />
      </md-card-actions>
    </md-card>

    <md-empty-state v-else v-bind="getEmptyProps()"></md-empty-state>
  </div>
</template>

<script>
import PurchaseOrderCard from "./PurchaseOrderCard";
import Filters from "./Filters";
import PurchaseOrderActions from "./PurchaseOrderActions";

import warehouseService from "@/services/purchase/warehouse";
import usesAccountInfoMixin from "@/mixins/usesAccountInfo";

import { mapPermissions } from "@/plugins/accountPermissions/";

const PARCIAL_ENTRY_MAIL_HEADER = (folio) =>
  `A los siguientes insumos de la orden de compra ${folio} se les dará entrada parcial`;

export default {
  name: "InputFromPurchaseOrder",
  components: {
    Filters,
    PurchaseOrderCard,
    PurchaseOrderActions,
  },
  mixins: [usesAccountInfoMixin],
  data() {
    return {
      loading: true,
      filterParams: { warehouse: null, purchaseOrder: null },
      isTransfer: false,
      catalogs: {
        warehouses: [],
        purchaseOrders: [],
      },
      currentPO: {
        suppliesConsolidated: [],
      },
      actionsPayload: {
        parcialInputsMode: false,
      },
    };
  },
  computed: {
    ...mapPermissions("Materials_Quotation", "supplycamp"),
    ...mapPermissions("Materials_Quotation", "materialsquotation", ["add"]),
    ...mapPermissions("Materials_Quotation", "materialsquotationsupply", [
      "add",
    ]),
    ...mapPermissions("Materials_Quotation", "quotationrequisition", ["view"]),

    hasNoPhysicalEntries() {
      return this.currentPO.suppliesConsolidated.every(
        ({ quantityPhysical }) => quantityPhysical === 0
      );
    },
    hasAllPhysicalEntries() {
      return this.currentPO.suppliesConsolidated.every(
        ({ quantityPhysical, quantity }) => quantityPhysical === quantity
      );
    },

    validReceivedAmount() {
      return !this.currentPO.suppliesConsolidated.some(
        ({ realReceivedAmount, totalEntries }) => {
          return +realReceivedAmount < 0 || +realReceivedAmount > totalEntries;
        }
      );
    },
    validRemainingQuantity() {
      return !this.currentPO.suppliesConsolidated.some(
        ({ quantity, quantityPhysical, receivedQuantity }) => {
          const total = +receivedQuantity + quantityPhysical;
          return total > quantity || +receivedQuantity < 0;
        }
      );
    },
    selectionComment: {
      get() {
        const parcialEntrySupplies = this.currentPO.suppliesConsolidated.filter(
          ({ realReceivedAmount }) => realReceivedAmount > 0
        );
        const { suppliesConsolidated: allSupplies, folio: poFolio } =
          this.currentPO;
        const prefix = parcialEntrySupplies.length
          ? PARCIAL_ENTRY_MAIL_HEADER(poFolio)
          : "";
        const quantity = parcialEntrySupplies.length
          ? ` (${parcialEntrySupplies.length} de ${allSupplies.length}) :`
          : "";
        return `${prefix + quantity}\n\n${parcialEntrySupplies
          .map((s) => `${s.name}`)
          .join("\n")}\n`;
      },
    },
  },
  methods: {
    fetchPurchaseOrder() {
      const selected = this.catalogs.purchaseOrders.find(
        ({ id }) => id === this.filterParams.purchaseOrder
      );
      warehouseService
        .fetchPurchaseOrder(selected.folio, true)
        .then((response) => {
          this.isTransfer = selected.folio.startsWith("TR");
          this.currentPO = response;
        })
        .catch(this.$fireErrorResponse);
    },
    resetPOState() {
      this.actionsPayload.parcialInputsMode = false;
    },

    sendData({ comment }) {
      this.loading = true;
      const { suppliesConsolidated } = this.currentPO;
      const { parcialInputsMode } = this.actionsPayload;
      const { hasNoPhysicalEntries, hasAllPhysicalEntries, isTransfer } = this;

      let payload = {};
      const supplies = suppliesConsolidated.filter(
        ({ realReceivedAmount }) => realReceivedAmount >= 0
      );
      if ((hasNoPhysicalEntries && !parcialInputsMode) || isTransfer) {
        payload = {
          comment: "",
          supplies: suppliesConsolidated,
          validation: true,
        };
      } else if (hasAllPhysicalEntries) {
        payload = {
          comment,
          validation: true,
        };
      } else if (parcialInputsMode && hasNoPhysicalEntries) {
        payload = {
          comment,
          supplies,
          validation: false,
        };
      } else {
        payload = {
          supplies: suppliesConsolidated.filter(
            ({ realReceivedAmount, receivedQuantity }) =>
              +realReceivedAmount > 0 || +receivedQuantity > 0
          ),
        };
      }
      let entryPromise = () =>
        isTransfer ||
        (hasNoPhysicalEntries && !parcialInputsMode) ||
        hasAllPhysicalEntries
          ? warehouseService.sendTransferEntry(this.currentPO.folio, payload)
          : warehouseService.sendPurchaseOrderEntries(
              this.currentPO.folio,
              payload
            );

      entryPromise()
        .then(() => {
          this.$successMessage({ title: "Entrada de inventario registrada" });
          this.fetchPurchaseOrder();
        })
        .catch(this.$fireErrorResponse)
        .finally(() => (this.loading = false));
    },
    changeDeliveryDate(e) {
      if (e) {
        this.loading = true;
        warehouseService
          .updateDeliveryDateODC({
            ...this.currentPO,
            expected_delivery_date: e,
          })
          .then(() => {})
          .catch(this.$fireErrorResponse)
          .finally(() => (this.loading = false));
      }
    },
    toolbarClass() {
      const { residentValidation } = this.currentPO;
      const { hasNoPhysicalEntries, hasAllPhysicalEntries } = this;

      return residentValidation
        ? "md-success"
        : hasAllPhysicalEntries
        ? "md-warning"
        : hasNoPhysicalEntries
        ? "md-info"
        : "md-transparent";
    },
    toolbarMessage() {
      const { residentValidation } = this.currentPO;
      const { hasNoPhysicalEntries, hasAllPhysicalEntries } = this;

      return residentValidation
        ? "Orden cerrada"
        : hasAllPhysicalEntries
        ? "(Se realizaron todas las entradas. Se puede cerrar la orden)"
        : hasNoPhysicalEntries
        ? "(Se necesita confirmar disponibilidad)"
        : "Hay entradas incompletas";
    },
    getEmptyProps() {
      const { warehouse } = this.filterParams;
      const { purchaseOrders } = this.catalogs;
      let message = "";
      const icon = !!warehouse && !purchaseOrders.length ? "info" : "category";
      if (warehouse) {
        message = !!purchaseOrders.length
          ? "Seleccione orden de compra"
          : "No hay ordenes de compra";
      } else {
        message = "Seleccione un almacén";
      }
      return {
        "md-icon": icon,
        "md-label": message,
      };
    },
  },
  watch: {
    "filterParams.warehouse"(newVal) {
      this.loading = true;
      warehouseService
        .fetchPurchaseOrdersByWarehouse(newVal)
        .then((response) => (this.catalogs.purchaseOrders = response))
        .catch(this.$fireErrorResponse)
        .finally(() => {
          this.filterParams.purchaseOrder = 0;
          this.loading = false;
        });
    },
    "filterParams.purchaseOrder"(value) {
      this.resetPOState();
      if (value) {
        this.fetchPurchaseOrder();
      }
    },
    "actionsPayload.parcialInputsMode"() {
      this.currentPO.suppliesConsolidated =
        this.currentPO.suppliesConsolidated.map((supply) => ({
          ...supply,
          realReceivedAmount: 0,
        }));
    },
    selectionComment: {
      immediate: true,
      handler(value) {
        this.reviComment = value;
      },
    },
  },
  mounted() {
    warehouseService
      .fetchiWarehouse()
      .then((response) => (this.catalogs.warehouses = response))
      .catch(this.$fireErrorResponse)
      .finally(() => (this.loading = false));
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/Purchases/InputFromPurchaseOrder/index.vue